import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaSignOutAlt, FaCalendarAlt, FaChartBar, FaUsers, FaFingerprint,
  FaHome, FaEdit, FaPlaneDeparture, FaBell, FaCog, FaFileAlt,
  FaClipboardList, FaUserCircle, FaBars, FaTimes, FaShieldAlt,
  FaMoon, FaSun,
} from 'react-icons/fa';
import api, { clearSession, getUser } from '../api';
import { ROLES, ROLE_LABELS, MANAGEMENT, LEAVE_APPROVERS, OVERSIGHT, hasRole } from './attendance/roles';
import dreamLogo from '../assets/dream.jpeg';

/**
 * Every navigable screen with the roles allowed to reach it. The same table
 * drives the desktop bar, the mobile drawer and the route guard.
 */
export const NAV_ITEMS = [
  { name: 'My Attendance', path: '/dashboard', icon: <FaHome />, roles: 'all' },
  { name: 'My Report', path: '/monthly-report', icon: <FaCalendarAlt />, roles: 'all' },
  { name: 'Leave', path: '/leave-management', icon: <FaPlaneDeparture />, roles: 'all' },
  { name: 'Overview', path: '/manager-dashboard', icon: <FaChartBar />, roles: OVERSIGHT },
  { name: 'Reports', path: '/reports', icon: <FaFileAlt />, roles: OVERSIGHT },
  { name: 'Staff', path: '/employee-management', icon: <FaUsers />, roles: OVERSIGHT },
  { name: 'Daily Entry', path: '/attendance-entry', icon: <FaEdit />, roles: OVERSIGHT },
  { name: 'Rural Reports', path: '/rural-submissions', icon: <FaClipboardList />, roles: OVERSIGHT },
  { name: 'Weekly Sheet', path: '/weekly-submission', icon: <FaCalendarAlt />, roles: OVERSIGHT },
  { name: 'Biometric', path: '/biometric-integration', icon: <FaFingerprint />, roles: MANAGEMENT },
  { name: 'Audit Trail', path: '/audit-trail', icon: <FaShieldAlt />, roles: MANAGEMENT },
  { name: 'Settings', path: '/system-settings', icon: <FaCog />, roles: [ROLES.ADMIN] },
];

export const canAccess = (role, item) =>
  item.roles === 'all' || hasRole(role, item.roles);

/** Bell with the unread count, polled while the tab is open. */
const NotificationBell = () => {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('notifications/unread-count/');
      setCount(data.count || 0);
    } catch {
      /* a failed poll should not disturb the page */
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => clearInterval(timer);
  }, [refresh]);

  return (
    <Link
      to="/notifications"
      className="relative p-2.5 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors dark:text-slate-200 dark:hover:bg-slate-800"
      aria-label={`Notifications${count ? `, ${count} unread` : ''}`}
    >
      <FaBell />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
};

const AttendanceNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : false;
  });
  const profileRef = useRef(null);

  useEffect(() => {
    setUser(getUser());
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('auth/logout/');
    } catch {
      /* the token is discarded locally regardless */
    }
    clearSession();
    navigate('/');
  };

  const role = user?.role;
  const links = NAV_ITEMS.filter((item) => canAccess(role, item));
  // The bar gets crowded for management roles, so overflow moves to the menu.
  const primary = links.slice(0, 5);
  const overflow = links.slice(5);

  const linkClass = (path) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap ${location.pathname === path
      ? 'bg-[#15A857]/10 text-[#15A857] font-semibold'
      : 'text-gray-600 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800'}`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg dark:bg-black dark:shadow-black/60 dark:border-b dark:border-slate-800">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/dashboard" className="flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700">
              <img src={dreamLogo} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-gray-800 dark:text-slate-100 leading-tight">Staff Attendance</h1>
              <p className="text-xs text-gray-500 dark:text-slate-400">Management System</p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {primary.map((item) => (
              <Link key={item.path} to={item.path} className={linkClass(item.path)}>
                {item.icon}
                <span className="text-sm">{item.name}</span>
              </Link>
            ))}
            {overflow.length > 0 && (
              <button
                onClick={() => setMenuOpen((open) => !open)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 text-sm dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaBars /> More
              </button>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setDarkMode((value) => !value)}
              aria-label="Toggle dark mode"
              className="p-2.5 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {darkMode ? <FaSun /> : <FaMoon />}
            </button>
            <NotificationBell />

            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen((open) => !open)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-gray-100 transition-colors dark:hover:bg-slate-800"
              >
                <FaUserCircle className="text-2xl text-gray-400 dark:text-slate-300" />
                <div className="hidden md:block text-right">
                  <div className="text-sm font-semibold text-gray-800 leading-tight dark:text-slate-100">
                    {user?.name || user?.username || 'User'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    {ROLE_LABELS[role] || 'Employee'}
                  </div>
                </div>
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 dark:bg-[#0b1220] dark:border-slate-700">
                  <div className="px-4 py-2 border-b dark:border-slate-700">
                    <div className="font-semibold text-gray-800 truncate dark:text-slate-100">
                      {user?.name || user?.username}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      {user?.employeeId} · {ROLE_LABELS[role] || 'Employee'}
                    </div>
                  </div>
                  <Link to="/my-profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-800">
                    My profile
                  </Link>
                  {hasRole(role, LEAVE_APPROVERS) && (
                    <Link to="/leave-management" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-800">
                      Leave approvals
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 dark:hover:bg-slate-800"
                  >
                    <FaSignOutAlt /> Sign out
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setMenuOpen((open) => !open)}
              className="lg:hidden p-2.5 rounded-xl text-gray-600 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800"
              aria-label="Menu"
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t bg-white shadow-lg max-h-[70vh] overflow-y-auto dark:bg-black dark:border-slate-800">
          <div className="container mx-auto px-4 sm:px-6 py-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1">
            {links.map((item) => (
              <Link key={item.path} to={item.path} className={linkClass(item.path)}>
                {item.icon}
                <span className="text-sm">{item.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default AttendanceNavbar;
